/**
 * Outcome F: crew hotel / overnight expense when an away overnight sticks.
 * Sim-friendly CAD planning figures — not a hotel API and not NS tax.
 *
 * Hotel posts only when overnightAtIcao is set, the stay actually stuck
 * (sandwich cancelled / next revenue from the field / parked latch with no
 * imminent home ferry), **and** the sim says the tail is parked at that field.
 * A scheduled sector the pilot never flew cannot invent a hotel night at its
 * destination. Parking dwell nights stay a separate line.
 */
import { icaoKey } from "./curfew";
import { hydrateCurrency, type CurrencyCode } from "./catalogs";
import { groundTicket, hotelBlock, type GroundingHold, type TicketGrounding } from "./flightGrounding";
import { ledgerIdempotencyKey, replaceTripHotelTxn, voidTripKindTxn } from "./ledger";
import { parkingDaysFromDwellHours, resolveParkingDwell, type GroundDwell } from "./parkingDwell";
import { overnightSticksAtField } from "./parkedOvernight";
import type { FusedPosition } from "./position";
import type { SimPhaseSample } from "./simDraft";
import type {
  AircraftSpec,
  CharterRequest,
  CompanyAccount,
  FlightLogEntry,
  LedgerTxn,
  Mission,
  SceneryAirport,
} from "./types";

/** One crew-overnight line (typical 2-pilot Hawker) — not per-room invoices. */
export const HOTEL_DEFAULT_NIGHT_CAD = 185;
export const HOTEL_METRO_NIGHT_CAD = 240;
export const HOTEL_CARIBBEAN_NIGHT_CAD = 210;

const METRO_ICAOS = new Set([
  "KBOS",
  "KJFK",
  "KEWR",
  "KTEB",
  "KHPN",
  "CYYZ",
  "CYUL",
  "CYVR",
  "KLAX",
  "KORD",
  "KMIA",
  "KDCA",
  "KIAD",
]);

export function hotelNightRateCad(icao: string): number {
  const code = icaoKey(icao);
  if (!code) return 0;
  if (METRO_ICAOS.has(code)) return HOTEL_METRO_NIGHT_CAD;
  if (code.startsWith("T") || code.startsWith("M") || code === "MYNN" || code === "TXKF") {
    return HOTEL_CARIBBEAN_NIGHT_CAD;
  }
  return HOTEL_DEFAULT_NIGHT_CAD;
}

export function hotelNightsForOvernight(dwell?: GroundDwell | null): number {
  if (!dwell) return 1;
  return Math.max(1, parkingDaysFromDwellHours(dwell.hours));
}

export function hotelCadFromNights(nightRateCad: number, nights: number): number {
  if (!(nightRateCad > 0) || nights <= 0) return 0;
  return Math.round(nightRateCad * nights * 100) / 100;
}

export function hotelMemo(icao: string, nights: number): string {
  const field = icaoKey(icao) || icao;
  return `Crew hotel ${field} · ${nights} night${nights === 1 ? "" : "s"}`;
}

function sameIcao(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = icaoKey(a);
  const right = icaoKey(b);
  return Boolean(left) && left === right;
}

/** Arrival that owns the stay — one hotel line per tail × field. */
export function hotelMissionForStay(
  missions: ReadonlyArray<Mission>,
  icao: string,
  aircraftId?: string,
): Mission | null {
  const field = icaoKey(icao);
  if (!field) return null;
  const rows = missions.filter((m) => {
    if (m.status === "cancelled") return false;
    if (m.flightType === "ferry") return false;
    if (aircraftId && m.aircraftId && m.aircraftId !== aircraftId) return false;
    return sameIcao(m.overnightAtIcao, field) || sameIcao(m.destIcao, field);
  });
  return (
    rows.find((m) => sameIcao(m.destIcao, field) && sameIcao(m.overnightAtIcao, field)) ??
    rows.find((m) => sameIcao(m.destIcao, field)) ??
    rows.find((m) => sameIcao(m.overnightAtIcao, field)) ??
    null
  );
}

/** Hotel rows are keyed by trip, with a tail × field fallback for older lines. */
function hotelTxnBelongsTo(
  txn: Pick<LedgerTxn, "missionId" | "aircraftId" | "memo">,
  owner: Pick<Mission, "id" | "aircraftId">,
  icao: string,
): boolean {
  if (txn.missionId === owner.id) return true;
  return Boolean(
    owner.aircraftId && txn.aircraftId === owner.aircraftId && /crew hotel/i.test(txn.memo) && txn.memo.includes(icao),
  );
}

export function quoteHotelForOvernight(input: {
  icao: string;
  homeIcao: string;
  dwell?: GroundDwell | null;
}): { hotelCad: number; nights: number; rateCad: number } {
  if (sameIcao(input.icao, input.homeIcao)) {
    return { hotelCad: 0, nights: 0, rateCad: 0 };
  }
  const rateCad = hotelNightRateCad(input.icao);
  const nights = hotelNightsForOvernight(input.dwell);
  return { hotelCad: hotelCadFromNights(rateCad, nights), nights, rateCad };
}

export function reconcileHotelLedger(input: {
  account: CompanyAccount;
  missions: ReadonlyArray<Mission>;
  logs?: ReadonlyArray<FlightLogEntry>;
  fleet?: ReadonlyArray<AircraftSpec>;
  inbox?: ReadonlyArray<CharterRequest>;
  simHistory?: ReadonlyArray<SimPhaseSample>;
  /** Live / last-known sim fix — hotel needs the tail to actually be there. */
  fused?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  homeIcao: string;
  homeCurrency?: CurrencyCode;
  now?: Date;
}): { account: CompanyAccount; updatedMissionIds: string[]; blocked: GroundingHold[] } {
  const now = input.now ?? new Date();
  const home = hydrateCurrency(input.homeCurrency ?? input.account.homeCurrency);
  const homeIcao = icaoKey(input.homeIcao);
  let account = input.account;
  const updatedMissionIds: string[] = [];
  const blocked: GroundingHold[] = [];
  const seen = new Set<string>();

  for (const mission of input.missions) {
    if (mission.status === "cancelled") continue;
    const icao = icaoKey(mission.overnightAtIcao);
    if (!icao || sameIcao(icao, homeIcao)) continue;
    const key = `${mission.aircraftId || mission.registration}:${icao}`;
    if (seen.has(key)) continue;
    if (
      !overnightSticksAtField({
        awayIcao: icao,
        homeIcao: homeIcao || input.homeIcao,
        aircraftId: mission.aircraftId,
        missions: input.missions,
        inbox: input.inbox,
        now,
      })
    ) {
      continue;
    }
    const owner = hotelMissionForStay(input.missions, icao, mission.aircraftId);
    if (!owner) continue;
    seen.add(key);

    const grounding: TicketGrounding = groundTicket({
      mission: owner,
      fused: input.fused,
      scenery: input.scenery,
      simHistory: input.simHistory,
      fleet: input.fleet,
      logs: input.logs,
      missions: input.missions,
      now,
    });
    const block = hotelBlock({
      icao,
      grounding,
      logs: input.logs,
      aircraft: input.fleet?.find((a) => a.id === owner.aircraftId || a.registration === owner.registration),
    });
    if (block) {
      const voided = voidTripKindTxn(account, "hotel", (t) => hotelTxnBelongsTo(t, owner, icao));
      account = voided.account;
      blocked.push({
        missionId: owner.id,
        flightNumber: grounding.flightNumber,
        kind: "hotel",
        icao,
        reason: block,
        voided: voided.removed.length > 0,
        status: `${voided.removed.length ? "HOTEL VOID" : "HOTEL HELD"}  ${grounding.flightNumber} ${icao} — ${block}`,
        atUtc: now.toISOString(),
      });
      continue;
    }

    const dwell = input.logs
      ? resolveParkingDwell({
          mission: owner,
          logs: input.logs,
          missions: input.missions,
          simHistory: input.simHistory,
          simAtIcao: grounding.presence.atIcao,
        })
      : null;
    const quoted = quoteHotelForOvernight({ icao, homeIcao: homeIcao || input.homeIcao, dwell });
    if (quoted.hotelCad <= 0) continue;
    const amountCad = -quoted.hotelCad;
    const memo = hotelMemo(icao, quoted.nights);
    const posted = account.transactions.filter((t) => t.kind === "hotel" && hotelTxnBelongsTo(t, owner, icao));
    const current = posted.reduce((sum, t) => sum + t.amountCad, 0);
    if (posted.length && Math.round(current * 100) === Math.round(amountCad * 100) && posted.every((t) => t.memo === memo)) {
      continue;
    }
    account = replaceTripHotelTxn(
      account,
      {
        createdAtUtc: now.toISOString(),
        kind: "hotel",
        amountCad,
        memo,
        missionId: owner.id,
        aircraftId: owner.aircraftId,
        currency: home,
        idempotencyKey: ledgerIdempotencyKey(owner.id, "hotel"),
      },
      now,
    );
    updatedMissionIds.push(owner.id);
  }

  return { account, updatedMissionIds, blocked };
}
